import React, { Component } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { change, stopSubmit } from 'redux-form';

import Grid from '@material-ui/core/Grid';

import { inChildren } from 'services/utils';

import {
  FormSearchSelectField,
  FormTextField,
  SimpleForm
} from 'components/Form';
import { useCan } from 'components/Rbac';
import Screen from 'components/Screen';

import * as actions from './actions';
import { validateForm } from './validation';

const defaultFormName = 'add_geo_streets';

class Container extends Component {
  handleCreate = async (data) => {
    const { loading, geoActions, dispatch, history = {} } = this.props;
    if (loading) return;
    try {
      await geoActions.createOne(data);
      history.goBack();
    } catch (err) {
      if (err && err.code === 'api/exist') {
        dispatch(stopSubmit(defaultFormName, { name: 'Уже существует' }));
      }
    }
  };

  render() {
    const { children, ...rest } = this.props;
    return inChildren(children, {
      ...rest,
      handleCreate: this.handleCreate
    });
  }
}

const mapStateToProps = (state, ownProps) => {
  const { resourceName = '' } = ownProps;
  const target = state[resourceName] || {};
  const { one = {} } = target;
  return {
    loading: one.loading
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  geoActions: bindActionCreators(actions, dispatch)
});

const ContainerConnect = connect(
  mapStateToProps,
  mapDispatchToProps
)(Container);

export const AddForm = (props) => {
  const dispatch = useDispatch();
  const [selectedRegionId, setSelectedRegionId] = React.useState(null);
  const [selectedDistrictId, setSelectedDistrictId] = React.useState(null);
  const [selectedLocalityId, setSelectedLocalityId] = React.useState(null);

  const {
    handleCreate,
    loading,
    formName = defaultFormName,
    footerBar = true
  } = props;

  const onChangeLocalityHandler = React.useCallback(
    (locality) => {
      setSelectedLocalityId(locality && locality.id ? locality.id : null);
      dispatch(change(formName, 'locality_district', ''));
    },
    [dispatch, formName]
  );

  const onChangeDistrictHandler = React.useCallback(
    (district) => {
      setSelectedDistrictId(district && district.id ? district.id : null);
      dispatch(change(formName, 'locality_id', null));
      onChangeLocalityHandler(null);
    },
    [dispatch, formName, onChangeLocalityHandler]
  );

  const onChangeRegionHandler = React.useCallback(
    (region) => {
      setSelectedRegionId(region && region.id ? region.id : null);
      dispatch(change(formName, 'district_id', null));
      onChangeDistrictHandler(null);
    },
    [dispatch, formName, onChangeDistrictHandler]
  );

  const history = useHistory();
  const back = history.goBack;

  const is_writable = useCan('geo:write');

  if (!is_writable) {
    return null;
  }

  return (
    <SimpleForm
      loading={loading}
      form={formName}
      onSubmit={handleCreate}
      disabled={loading}
      onBack={back}
      direction="row"
      footerBar={footerBar}
      validate={validateForm}
      initialValues={{ weight: '0' }}
      parse={(values) => {
        const { name, alt_name, old_name, name_lang1, name_lang2 } = values;
        const res = {
          name: name ? name.trim() : '',
          alt_name: alt_name ? alt_name.trim() : '',
          old_name: old_name ? old_name.trim() : '',
          name_lang1: name_lang1 ? name_lang1.trim() : '',
          name_lang2: name_lang2 ? name_lang2.trim() : '',
          locality_id: values.locality_id ? values.locality_id.id : null,
          weight: values.weight ? parseInt(values.weight) : 0,
          locality_district: values.locality_district
            ? values.locality_district.trim()
            : ''
        };
        return res;
      }}>
      <Grid item xs={12}>
        <FormTextField source="name" label="Название" disabled={loading} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormSearchSelectField
          source="region_id"
          label="Регион"
          disabled={loading}
          requestPath="/geo/regions"
          responseFormat={(v) => v.results}
          renderOption={(it) => it.name}
          searchBy="name"
          onChangeHandler={onChangeRegionHandler}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormSearchSelectField
          source="district_id"
          label="Район"
          disabled={loading || !selectedRegionId}
          requestPath="/geo/district"
          responseFormat={(v) => v.results}
          renderOption={(it) => it.name}
          searchBy="name"
          where={selectedRegionId ? { region_id: selectedRegionId } : null}
          onChangeHandler={onChangeDistrictHandler}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormSearchSelectField
          source="locality_id"
          label="Нас. пункт"
          disabled={loading || !selectedRegionId}
          requestPath="/geo/locality"
          responseFormat={(v) => v.results}
          renderOption={(it) => it.name}
          searchBy="name"
          where={{
            region_id: selectedRegionId || undefined,
            district_id: selectedDistrictId || undefined
          }}
          onChangeHandler={onChangeLocalityHandler}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          source="locality_district"
          label="Район нас. пункта"
          disabled={loading || !selectedLocalityId}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          source="alt_name"
          label="Альтернативное название"
          disabled={loading}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          source="old_name"
          label="Старое название"
          disabled={loading}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField source="name_lang1" label="Язык №1" disabled={loading} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          source="name_lang2"
          label="Язык №2:"
          disabled={loading}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          source="weight"
          label="Вес"
          disabled={loading}
          type="number"
          inputProps={{
            step: '1'
          }}
        />
      </Grid>
    </SimpleForm>
  );
};

const StreetAdd = (props) => (
  <Screen title="Добавить улицу">
    <ContainerConnect {...props}>
      <AddForm />
    </ContainerConnect>
  </Screen>
);

export default StreetAdd;
